import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { z } from "zod";
import { useSession } from "@/hooks/useSession";
import { supabase, API_URL } from "@/lib/supabase";
import { posthog } from "@/lib/posthog";
import { Logo } from "@/components/Logo";

export const Route = createFileRoute("/upgrade/success")({
  validateSearch: z.object({ session_id: z.string().optional() }),
  component: UpgradeSuccessPage,
});

function UpgradeSuccessPage() {
  const { session_id } = Route.useSearch();
  const { session, loading: sessionLoading } = useSession();
  const navigate = useNavigate();
  const [error, setError] = useState(false);

  useEffect(() => {
    if (sessionLoading || !session || !session_id) return;

    supabase.auth.getSession().then(({ data: { session: fresh } }) => {
      fetch(`${API_URL}/api/verify-session`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${fresh?.access_token}`,
        },
        body: JSON.stringify({ session_id }),
      })
        .then((r) => r.json())
        .then((data) => {
          if (!data?.activated) {
            setError(true);
            return;
          }
          posthog.capture("upgrade_completed", { plan_id: data.plan_id });
          navigate({ to: "/alertas", search: { welcome: true } });
        })
        .catch(() => setError(true));
    });
  }, [session, sessionLoading, session_id]);

  if (sessionLoading) return null;

  if (!session_id || !session || error) {
    return (
      <PageShell>
        <h1 className="font-display text-xl text-foreground">Pagamento em processamento</h1>
        <p className="mt-3 text-sm text-muted-foreground">
          Assim que confirmado, seu acesso será ativado automaticamente. Verifique seu e-mail para
          acompanhar o status.
        </p>
        <Link to="/" className="mt-8 inline-block text-sm text-primary hover:underline">
          Voltar ao início
        </Link>
      </PageShell>
    );
  }

  return (
    <PageShell>
      <div className="mx-auto mb-5 h-8 w-8 animate-spin rounded-full border-2 dark:border-white/10 border-black/10 border-t-primary" />
      <h1 className="font-display text-xl text-foreground">Confirmando pagamento…</h1>
      <p className="mt-3 text-sm text-muted-foreground">Aguarde um instante.</p>
    </PageShell>
  );
}

function PageShell({ children }: { children: React.ReactNode }) {
  return (
    <div className="min-h-screen text-foreground">
      <nav className="mx-auto flex max-w-5xl items-center justify-between px-6 py-6">
        <Link to="/" className="text-foreground" aria-label="Busca Blindados">
          <Logo className="h-6 w-auto md:h-10" />
        </Link>
      </nav>
      <main className="mx-auto max-w-md px-6 pb-32 pt-10">
        <div className="surface-card rounded-3xl px-8 py-12 text-center">{children}</div>
      </main>
    </div>
  );
}
